import {
  type MemberRow,
  isRowBlocked,
  isRowSendable,
} from '@/features/OrgMembers/lib/memberRows'
import type {
  AddOrganizationMemberLeftoverKind,
  AddOrganizationMemberOutcome,
  AddOrganizationMemberResult,
} from '@/generated/http-clients/v3ApiSchemas'

export type MemberOutcome =
  | { kind: 'sent'; row: MemberRow; result: AddOrganizationMemberResult }
  | { kind: 'notSent'; row: MemberRow }
  | { kind: 'missing'; row: MemberRow }

export type OutcomeSummary = {
  added: number
  alreadyMember: number
  blocked: number
  failed: number
}

export type MessageKey = {
  key: string
  values?: Record<string, string>
}

const KEY_PREFIX = 'orgDetails.members.addMembers'

const ADDED_OUTCOMES: ReadonlySet<AddOrganizationMemberOutcome> = new Set([
  'CREATED',
  'ATTACHED_EXISTING',
])

const SUMMARY_BUCKETS: Record<
  AddOrganizationMemberOutcome,
  keyof OutcomeSummary
> = {
  CREATED: 'added',
  ATTACHED_EXISTING: 'added',
  ALREADY_MEMBER: 'alreadyMember',
  BLOCKED_SSO: 'blocked',
  BLOCKED_UNVERIFIED: 'blocked',
  FAILED: 'failed',
}

export const LEFTOVER_KIND_KEYS: Record<
  AddOrganizationMemberLeftoverKind,
  string
> = {
  AUTH0_USER: `${KEY_PREFIX}.results.leftoverKinds.AUTH0_USER`,
  STRIPE_CUSTOMER: `${KEY_PREFIX}.results.leftoverKinds.STRIPE_CUSTOMER`,
  ACCOUNT: `${KEY_PREFIX}.results.leftoverKinds.ACCOUNT`,
}

export function pairResultsWithRows(
  submittedRows: readonly MemberRow[],
  results: readonly AddOrganizationMemberResult[],
): MemberOutcome[] {
  const outcomes: MemberOutcome[] = []
  let sentIndex = 0
  for (const row of submittedRows) {
    if (isRowBlocked(row)) {
      outcomes.push({ kind: 'notSent', row })
      continue
    }
    if (!isRowSendable(row)) {
      continue
    }
    const result = results[sentIndex]
    sentIndex += 1
    outcomes.push(
      result ? { kind: 'sent', row, result } : { kind: 'missing', row },
    )
  }
  return outcomes
}

function summaryBucket(outcome: MemberOutcome): keyof OutcomeSummary {
  switch (outcome.kind) {
    case 'notSent':
      return 'blocked'
    case 'missing':
      return 'failed'
    default:
      return SUMMARY_BUCKETS[outcome.result.outcome]
  }
}

export function summarizeOutcomes(
  outcomes: readonly MemberOutcome[],
): OutcomeSummary {
  const summary: OutcomeSummary = {
    added: 0,
    alreadyMember: 0,
    blocked: 0,
    failed: 0,
  }
  for (const outcome of outcomes) {
    summary[summaryBucket(outcome)] += 1
  }
  return summary
}

function isAdded(result: AddOrganizationMemberResult): boolean {
  return ADDED_OUTCOMES.has(result.outcome)
}

export function isEveryMemberAdded(
  outcomes: readonly MemberOutcome[],
): boolean {
  return (
    outcomes.length > 0 &&
    outcomes.every(
      (outcome) => outcome.kind === 'sent' && isAdded(outcome.result),
    )
  )
}

function mayBeInOrg(outcome: MemberOutcome): boolean {
  if (outcome.kind === 'missing') {
    return true
  }
  if (outcome.kind !== 'sent') {
    return false
  }
  return (
    isAdded(outcome.result) ||
    (outcome.result.outcome === 'FAILED' &&
      outcome.result.reason === 'ROLE_ASSIGNMENT_FAILED')
  )
}

export function shouldRefreshMembers(
  outcomes: readonly MemberOutcome[],
): boolean {
  return outcomes.some(mayBeInOrg)
}

export function outcomeMessageKey(result: AddOrganizationMemberResult): string {
  if (isAdded(result)) {
    if (result.emailSent === false) {
      return `${KEY_PREFIX}.results.addedEmailFailed`
    }
    return `${KEY_PREFIX}.results.added`
  }
  if (result.reason === 'ORG_SSO_ENABLED') {
    return `${KEY_PREFIX}.ssoEnabled`
  }
  if (result.reason) {
    return `${KEY_PREFIX}.results.reasons.${result.reason}`
  }
  return `${KEY_PREFIX}.results.outcomes.${result.outcome}`
}

export const MISSING_RESULT_KEY = `${KEY_PREFIX}.results.missing`

export function blockedRowMessageKey(row: MemberRow): MessageKey | undefined {
  switch (row.status) {
    case 'alreadyMember':
      return { key: `${KEY_PREFIX}.row.alreadyMember` }
    case 'ssoDomainNoIdentity':
      if (row.ssoDomainClaim) {
        return {
          key: `${KEY_PREFIX}.row.ssoDomainNoIdentity`,
          values: { connection: row.ssoDomainClaim.connectionName },
        }
      }
      return { key: `${KEY_PREFIX}.row.ssoDomainNoIdentityUnnamed` }
    case 'noSelectableIdentity':
      return { key: `${KEY_PREFIX}.row.noSelectableIdentity` }
    case 'lookupFailed':
      return { key: `${KEY_PREFIX}.row.lookupFailed` }
    default:
      return undefined
  }
}
